/**
 * "Add a ..." (T387 M5): the SAME control on every list, asking
 * kind-appropriate questions — a place (airport towns), a route (golf hub +
 * course), a venue / destination (weddings, tours). Registry bands only;
 * blank bands are honestly unpriced (at least one required); the note is
 * required. Saves as provisional gold via POST /api/rate-entry. A duplicate
 * 409s into edit-instead (the caller filters the list to the existing row).
 */
import { useState } from 'preact/hooks';
import { addRateEntry, type PriceEntryKind } from '../../lib/actions.js';
import { Q36_NEW_ENTRY_CEILING_CENTS } from '../../lib/q36.js';
import { bands, type Registry } from '../../store/registry.js';

/** The one add control's label per kind (per-list noun). */
export const ADD_LABELS: Readonly<Record<string, string>> = {
  town: 'Add a place',
  route: 'Add a route',
  venue: 'Add a venue',
};

/**
 * What a saved entry actually did (T608 M8b, R8). Replaces:
 *
 *   "Added to the price list, awaiting confirmation. A colleague confirms the
 *    price with one note."
 *
 * Both halves of which were false, measured. (1) There is no colleague
 * mechanism: insertRateEntry's `actor` and ratifyRate's `ratifiedBy` are both
 * `session.user` and are never compared, `session.user` is operator-typed free
 * text falling back to 'office' (server/auth.ts), and Session has no role field —
 * one person adds and confirms by typing a different name. (2) "Awaiting
 * confirmation" implies the price is parked; it is LIVE. buildSnapshot's rate read
 * carries no predicate (core/data/rate-snapshot.ts) and getRateCents has no
 * `ratified` filter (core/data/rates-repo.ts). The only thing `ratified` gates is
 * the estimator fit set (rate-snapshot.ts, `operator_added = FALSE OR ratified =
 * TRUE`) — which is why the third sentence names that and not "nothing happens".
 *
 * The failure mode the old copy caused: an operator who fat-fingers a price
 * believing a colleague will check it has been told by our own UI to leave a live
 * wrong price in the system.
 *
 * Every clause here is pinned: "prices jobs straight away" by test/server/
 * rate-entry.spec.ts (add an entry, quote it, unratified), the fit-set clause by
 * test/core/rate-snapshot.spec.ts. Building a REAL two-person control is a filed
 * finding, not this change.
 *
 * SCOPE (T608 M8b, output-gate correction): this is the UNIVERSALLY true part
 * only. The fit-set sentence moved to ESTIMATES_CLAUSE below because it is true
 * for airport towns and FALSE for golf routes and venues — see there.
 *
 * NOUN (T608 M8 remediation): the third sentence names the state by the string
 * the operator can actually SEE. `provisional` is an internal flag name and is
 * rendered NOWHERE in the client — measured: every visible label is "Awaiting
 * confirmation" (admin/RateList.tsx, admin/QueueView.tsx) and the Shelf chip reads
 * "{n} to review" (admin/Shelf.tsx). Alex ruled that map explicitly
 * ("Provisional -> Awaiting confirmation",
 * docs/plans/inline-2026-07-17-rates-book-polish-p1-2e9d.md:46), with the
 * mitigation being that the gold row label carries the awaiting-confirmation
 * signal. Telling her a state name no screen shows leaves her looking for a word
 * that is not there.
 */
const SAVED_MESSAGE =
  'Added to the price list. It prices jobs straight away. '
  + 'Until someone confirms it with a note it stays marked “Awaiting confirmation”.';

/**
 * What "provisional" COSTS — and it costs it for `kind: 'town'` only
 * (T608 M8b, output-gate correction).
 *
 * The sentence this replaces was glued onto SAVED_MESSAGE for every kind:
 * "Until someone confirms it with a note it stays marked provisional, AND it is
 * left out of the estimates for unlisted places." That construction tells a golf
 * or wedding operator that confirming will admit her price to the estimates. It
 * never will. buildModel (core/data/rate-snapshot.ts) fits the distance model with
 * `WHERE r.service = 'airport'` as its primary discriminator, so a golf / wedding /
 * private-tour row is excluded PERMANENTLY, confirmed or not — pinned by
 * test/core/rate-snapshot.spec.ts, "golf rates never enter the airport fit set,
 * ratified or not" (that pin seeds `ratified: true` precisely to prove the
 * permanence). Only for a town does `ratified` decide, which is the pair of pins
 * directly above it in the same file.
 *
 * ALSO withheld when the server measured `searchable: false`. Not a softening —
 * a coordless / absent `locations` row fails buildModel's JOIN + its
 * `road_km_to_dub_airport IS NOT NULL` predicate, so that town is out of the fit
 * set permanently too, exactly like golf. Withholding the clause asserts nothing;
 * NOT_IN_SEARCH_MESSAGE says the true thing instead.
 *
 * The unconfirmed STATE stays true for every kind — what it is not is called
 * "provisional" anywhere the operator can see. Corrected T608 M8 remediation: this
 * comment used to justify the word by claiming it WAS the RateList badge, and that
 * was false about the rendered string. Measured, the three surfaces the state
 * really reaches are the RateList row line "Awaiting confirmation"
 * (admin/RateList.tsx), the Shelf chip "{n} to review" (admin/Shelf.tsx) and the
 * "Needs a decision" queue arm, whose card also reads "Awaiting confirmation"
 * (admin/QueueView.tsx; server arm 1 in server/app.ts). `provisional` is the flag
 * name in the projection and in this file's own props — never a label.
 */
const ESTIMATES_CLAUSE =
  ' It is also left out of the estimates for unlisted places until then.';

/**
 * Appended when the server measured this town as unreachable from the kiosk
 * pickup search (T608 M8b, R8 — `searchable: false`).
 *
 * The silence this ends: POST /api/rate-entry writes `rates` + `rate_audit` and
 * nothing to `locations`, so an off-gazetteer airport town saves fine, reports
 * success, and can never be selected — searchLocations requires a coordinate and
 * nothing in the UI can supply one. Not unconditional: a gazetteer place already
 * has its row and its coordinates, so the identical form works end to end and
 * says nothing extra.
 */
const NOT_IN_SEARCH_MESSAGE =
  ' This place is not in the kiosk pickup search, and adding a price does not put it there. '
  + 'Quote it by hand, and ask for the place itself to be added.';

/**
 * The ONE composer for the after-save notice, shared by AddEntryForm and
 * PromoteForm (T608 M8b, output-gate correction). Both call sites previously
 * assembled the message themselves with the same inline ternary; the kind gate
 * would have had to be duplicated too, and a copy that drifts per form is how
 * PromoteForm came to carry a byte-identical copy of the false
 * "a colleague confirms" sentence in the first place.
 *
 * `searchable` is the server's measured fact for a town (null for every other
 * kind — no `locations` row is involved).
 */
export function savedMessage(kind: PriceEntryKind, searchable: boolean | null): string {
  const town = kind === 'town';
  return SAVED_MESSAGE
    + (town && searchable !== false ? ESTIMATES_CLAUSE : '')
    + (searchable === false ? NOT_IN_SEARCH_MESSAGE : '');
}

export type AddEntryFormProps = {
  registry: Registry;
  service: string;
  kind: PriceEntryKind;
  /** ok + notice text; on a 409 `name` carries the duplicate to filter to. */
  onDone: (ok: boolean, message: string, name?: string) => void;
  /** Explicit way out (ux-review: a toggle-only close depends on memory). */
  onCancel: () => void;
};

export function AddEntryForm({ registry, service, kind, onDone, onCancel }: AddEntryFormProps) {
  const [name, setName] = useState('');
  const [hubName, setHubName] = useState('');
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [amountWarned, setAmountWarned] = useState(false);

  const nameLabel = kind === 'route' ? 'Course' : kind === 'town' ? 'Place name' : 'Venue name';

  const submit = async () => {
    if (!name.trim() || (kind === 'route' && !hubName.trim())) {
      onDone(false, kind === 'route' ? 'Enter the hub and the course names.' : `Enter the ${nameLabel.toLowerCase()}.`);
      return;
    }
    const bandPrices: Record<string, number> = {};
    for (const band of bands(registry)) {
      const raw = (prices[band] ?? '').trim();
      if (raw === '') continue; // honestly unpriced — the row shows no price for this band
      const euros = Number(raw);
      if (!Number.isFinite(euros) || euros <= 0) {
        onDone(false, `Enter a price in euro for band ${band}, or leave it blank.`);
        return;
      }
      bandPrices[band] = Math.round(euros * 100);
    }
    if (Object.keys(bandPrices).length === 0) {
      onDone(false, 'Enter at least one band price.');
      return;
    }
    if (!note.trim()) {
      onDone(false, 'A note is required when adding a price.');
      return;
    }
    // Q36 (T387 M6) warn-once: a new entry over the sanity ceiling is almost
    // always a missing decimal. Soft — save again to confirm; re-arms on edit.
    if (!amountWarned && Object.values(bandPrices).some((c) => c > Q36_NEW_ENTRY_CEILING_CENTS)) {
      setAmountWarned(true);
      onDone(false, 'That looks very high for a standing price. Check the amounts, then save again to confirm.');
      return;
    }
    const res = await addRateEntry({
      kind,
      service,
      name: name.trim(),
      ...(kind === 'route' ? { hubName: hubName.trim() } : {}),
      bandPrices,
      note: note.trim(),
    });
    if (res.ok) {
      onDone(true, savedMessage(kind, res.searchable));
    } else if (res.status === 409) {
      onDone(false, res.message, name.trim());
    } else {
      onDone(false, res.message);
    }
  };

  return (
    <div class="admin-add">
      {/* Horizontal band-column layout mirroring the list rows (T399 item 3):
          name | 1-4 | 4-8 | 8-12 | 12-16 | note | save. Accessible names are
          unchanged (name/note labels exact; band inputs keep "Price {band}"). */}
      <div class="admin-add-row">
        {kind === 'route' && (
          <div class="admin-add-field admin-add-field--name">
            <label class="field-label" for="add-hub">Start from (hub)</label>
            <input
              id="add-hub" class="login-input" type="text"
              value={hubName} onInput={(e) => setHubName((e.target as HTMLInputElement).value)}
            />
          </div>
        )}
        <div class="admin-add-field admin-add-field--name">
          <label class="field-label" for="add-name">{nameLabel}</label>
          <input
            id="add-name" class="login-input" type="text"
            value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)}
          />
        </div>
        {bands(registry).map((band) => (
          <div key={band} class="admin-add-field admin-add-field--band">
            <label class="field-label" for={`add-price-${band}`}>Price {band}</label>
            <input
              id={`add-price-${band}`} class="login-input" type="number" inputMode="decimal"
              aria-describedby="add-price-hint"
              value={prices[band] ?? ''}
              onInput={(e) => {
                setPrices({ ...prices, [band]: (e.target as HTMLInputElement).value });
                setAmountWarned(false); // Q36 re-arms on edit
              }}
            />
          </div>
        ))}
        <div class="admin-add-field admin-add-field--note">
          <label class="field-label" for="add-note">Note</label>
          <input
            id="add-note" class="login-input" type="text"
            value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="admin-add-actions">
          <button type="button" class="btn btn--secondary" onClick={() => void submit()}>Save to the price list</button>
          <button type="button" class="btn btn--quiet" onClick={onCancel}>Cancel</button>
        </div>
      </div>
      {/* ⛔⛔ T1749 M9 rework 2 (Captain's four-gate check) — THE HINT IS A FIELD
          HINT NOW, not a paragraph that happens to sit under a form. It kept the
          screen-local `.admin-add-hint` class and no `aria-describedby`, so a
          reader who landed on a band input was never told what a blank one MEANS
          — which is the one thing no label on this row states.
          ⛔ THE WIRING IS `FormField`'s, BY HAND, because neither of these two
          forms is on `FormField` and adopting it here is a layout rewrite this
          rework does not own: one `p.field-hint` with an id, referenced from
          every band input, which is exactly what `FormField` emits (`field-hint`
          + `aria-describedby`, `packages/ui/src/FormField.tsx:497-500`).
          ⚠️ BOTH CLAUSES STAY, and the euro one is measured rather than assumed:
          these band inputs are bare `login-input`, their labels read `Price 1-4`,
          and nothing on the row shows a euro. `PriceList` earns its shorter hint
          by saying it in the LABEL ("Price in euro"); `.login-input--euro` is a
          width in `app.css`, not a symbol. Give the row a euro affordance and
          this clause goes with it. */}
      <p class="field-hint" id="add-price-hint">Prices in euro. Leave a band blank if there's no set price.</p>
    </div>
  );
}
